import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../hooks/useAppSelector';
import {
  selectIsAuthenticated,
  selectIsInitialized,
  selectIsLoading,
  selectUserRole,
  selectUserFeatures,
  selectUserAccountId,
} from '../features/auth/authSelectors';
import {
  checkRole,
  checkPermission,
  checkFeature,
  checkAccess,
  checkUserManagement,
  checkSameAccount,
  Role,
} from '../utils/permissions';

// ===== BASIC PROTECTED ROUTE =====

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  redirectTo = '/login',
  fallback
}) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isInitialized = useAppSelector(selectIsInitialized);
  const isLoading = useAppSelector(selectIsLoading);
  const location = useLocation();

  // Show loading while checking auth
  if (!isInitialized || isLoading) {
    return fallback || (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // If route requires auth and user is not authenticated
  if (requireAuth && !isAuthenticated) {
    console.log('🔒 Access denied: User not authenticated');
    // Save the attempted URL to redirect back after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If route is for non-authenticated users (like login) and user is authenticated
  if (!requireAuth && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  console.log('✅ Access granted to:', location.pathname);
  return <>{children}</>;
};

// ===== ROLE PROTECTED ROUTE =====

interface RoleProtectedRouteProps extends ProtectedRouteProps {
  roles: Role | Role[];
  exact?: boolean;
}

export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  roles,
  exact = false,
  redirectTo = '/unauthorized',
  fallback,
  ...props
}) => {
  const userRole = useAppSelector(selectUserRole);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isInitialized = useAppSelector(selectIsInitialized);
  const location = useLocation();

  // First check basic auth
  if (!isInitialized || !isAuthenticated) {
    return (
      <ProtectedRoute {...props} redirectTo="/login" fallback={fallback}>
        {children}
      </ProtectedRoute>
    );
  }

  // Check role permission
  const hasRequiredRole = checkRole(userRole, roles, exact);

  if (!hasRequiredRole) {
    console.log(`🔒 Access denied: User role '${userRole}' does not have required roles:`, roles);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  console.log('✅ Role access granted to:', location.pathname);
  return <>{children}</>;
};

// ===== PERMISSION PROTECTED ROUTE =====

interface PermissionProtectedRouteProps extends ProtectedRouteProps {
  permissions: string | string[];
}

export const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({
  children,
  permissions,
  redirectTo = '/unauthorized',
  fallback,
  ...props
}) => {
  const userRole = useAppSelector(selectUserRole);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isInitialized = useAppSelector(selectIsInitialized);
  const location = useLocation();

  // First check basic auth
  if (!isInitialized || !isAuthenticated) {
    return (
      <ProtectedRoute {...props} redirectTo="/login" fallback={fallback}>
        {children}
      </ProtectedRoute>
    );
  }

  // Check permission
  const hasRequiredPermission = checkPermission(userRole, permissions);

  if (!hasRequiredPermission) {
    console.log(`🔒 Access denied: User role '${userRole}' does not have required permissions:`, permissions);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  console.log('✅ Permission access granted to:', location.pathname);
  return <>{children}</>;
};

// ===== FEATURE PROTECTED ROUTE =====

interface FeatureProtectedRouteProps extends ProtectedRouteProps {
  features: string | string[];
}

export const FeatureProtectedRoute: React.FC<FeatureProtectedRouteProps> = ({
  children,
  features,
  redirectTo = '/upgrade',
  fallback,
  ...props
}) => {
  const userFeatures = useAppSelector(selectUserFeatures);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isInitialized = useAppSelector(selectIsInitialized);
  const location = useLocation();

  // First check basic auth
  if (!isInitialized || !isAuthenticated) {
    return (
      <ProtectedRoute {...props} redirectTo="/login" fallback={fallback}>
        {children}
      </ProtectedRoute>
    );
  }

  // Check feature access
  const hasRequiredFeature = checkFeature(userFeatures, features);

  if (!hasRequiredFeature) {
    console.log(`🔒 Access denied: User does not have required features:`, features);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  console.log('✅ Feature access granted to:', location.pathname);
  return <>{children}</>;
};

// ===== RESOURCE PROTECTED ROUTE =====

interface ResourceProtectedRouteProps extends ProtectedRouteProps {
  resource: string;
  action?: string;
}

export const ResourceProtectedRoute: React.FC<ResourceProtectedRouteProps> = ({
  children,
  resource,
  action = 'read',
  redirectTo = '/unauthorized',
  fallback,
  ...props
}) => {
  const userRole = useAppSelector(selectUserRole);
  const userFeatures = useAppSelector(selectUserFeatures);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isInitialized = useAppSelector(selectIsInitialized);
  const location = useLocation();

  // First check basic auth
  if (!isInitialized || !isAuthenticated) {
    return (
      <ProtectedRoute {...props} redirectTo="/login" fallback={fallback}>
        {children}
      </ProtectedRoute>
    );
  }

  // Check resource access
  const hasResourceAccess = checkAccess(userRole, userFeatures, resource, action);

  if (!hasResourceAccess) {
    console.log(`🔒 Access denied: User cannot ${action} resource '${resource}'`);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  console.log('✅ Resource access granted to:', location.pathname);
  return <>{children}</>;
};

// ===== ACCOUNT PROTECTED ROUTE =====

interface AccountProtectedRouteProps extends ProtectedRouteProps {
  accountId?: string;
  allowSuperadmin?: boolean;
}

export const AccountProtectedRoute: React.FC<AccountProtectedRouteProps> = ({
  children,
  accountId,
  allowSuperadmin = true,
  redirectTo = '/unauthorized',
  fallback,
  ...props
}) => {
  const userRole = useAppSelector(selectUserRole);
  const userAccountId = useAppSelector(selectUserAccountId);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isInitialized = useAppSelector(selectIsInitialized);
  const location = useLocation();

  // First check basic auth
  if (!isInitialized || !isAuthenticated) {
    return (
      <ProtectedRoute {...props} redirectTo="/login" fallback={fallback}>
        {children}
      </ProtectedRoute>
    );
  }

  // Use user's account if no specific account provided
  const targetAccountId = accountId || userAccountId;

  if (!targetAccountId) {
    console.log('🔒 Access denied: No account ID available');
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Allow superadmin to access any account
  if (allowSuperadmin && userRole === 'superadmin') {
    console.log('✅ Superadmin access granted to:', location.pathname);
    return <>{children}</>;
  }

  // Check if user is in the same account
  const isSameAccount = checkSameAccount(userAccountId, targetAccountId);

  if (!isSameAccount) {
    console.log(`🔒 Access denied: User not in account '${targetAccountId}'`);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  console.log('✅ Account access granted to:', location.pathname);
  return <>{children}</>;
};

export default ProtectedRoute;